import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as goldInitialState } from '~redux/slices/gold';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { TGoldConversionFiltersRedux } from '~types/feat/gold';

import * as Styles from './_GoldConversionSearchAndFilterBar.module.scss';
import dayjs from 'dayjs';
import { GoldConversionStatusEnum, MetalTypeEnum } from '~utils/enums';

const GoldConversionSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.gold.goldConversion.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const initialFilters = goldInitialState.goldConversion.filters;

    const setFilters = (inputFilters: TGoldConversionFiltersRedux) => dispatch(combinedActions.goldSetGoldConversionFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.goldGetGoldConversionListingAttempt());
    const handleCancelClick = () => {
        setFilters({
            ...initialFilters,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
        });
        dispatch(combinedActions.goldGetGoldConversionListingAttempt());
    };

    useEffect(() => {
        if (branchesList.length === 0) return;

        if (allBranchAccess) {
            setFilters({
                ...filters,
                branchName: '',
                branchCode: '',
            });
            return;
        }

        const defaultBranch = branchesList.find(b => b.branchCode === branchCode);

        if (!defaultBranch) return;

        setFilters({
            ...filters,
            branchName: defaultBranch.branchName,
            branchCode: defaultBranch.branchCode,
        });
    }, [branchesList]);

    useEffect(() => {
        return () => {
            setFilters({
                ...initialFilters,
                branchCode: !allBranchAccess ? branchCode : '',
                branchName: !allBranchAccess ? (branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '') : '',
            });
        };
    }, []);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];
        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Customer Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.customerName || '',
                setValue: (event) => setFilters({ ...filters, customerName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'ID No.:',
            type: 'input' as const,
            inputParams: {
                value: filters.idNo?.toString() || '',
                setValue: (event) => setFilters({ ...filters, idNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedBranch = branchesList.find(
                        (branch) => branch.branchName === event.target.value,
                    );

                    setFilters({
                        ...filters,
                        branchName: event.target.value,
                        branchId: selectedBranch?.branchId || undefined,
                    });
                },
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: GoldConversionStatusEnum[GoldConversionStatusEnum.Approved],
                        value: GoldConversionStatusEnum.Approved,
                    },
                    {
                        label: GoldConversionStatusEnum[GoldConversionStatusEnum.Pending],
                        value: GoldConversionStatusEnum.Pending,
                    },
                    {
                        label: GoldConversionStatusEnum[GoldConversionStatusEnum.Rejected],
                        value: GoldConversionStatusEnum.Rejected,
                    },
                ],
                value: filters.status || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, status: event.target.value }),
                optionSelected: !!GoldConversionStatusEnum[filters.status as keyof typeof GoldConversionStatusEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Gold',
                        value: MetalTypeEnum.Gold,
                    },
                    {
                        label: 'Silver',
                        value: MetalTypeEnum.Silver,
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, metalType: event.target.value }),
                optionSelected: !!MetalTypeEnum[filters.metalType as keyof typeof MetalTypeEnum],
                noOptionsMessage: 'No metal type found',
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom || '',
                setDate: (date: string) => {
                    if (filters.dateTo && date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo || '',
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default GoldConversionSearchAndFilterBar;
